import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Divider } from '@mui/material';

import useFormik from 'hooks/useFormik';
import { DocumentImageViewer } from '../DocumentImageViewer';
import { DocumentReviewHeader } from '../DocumentViewHeader';
import { DriverLicenseForm } from '../DriverLicenseForm';
import { IDocumentDetailField } from 'common/document/interfaces/document-detail-field';
import { IDocument } from 'common/document/interfaces/document';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { originalSuffix } from 'common/document/constants/original-suffix';
import { LogEvents } from 'components/Logs/events';
import {
  DocumentStatusEnum,
  readOnlyViewableStatuses,
} from 'common/document/enums/document-status.enum';
import { DocumentFiles } from 'common/document/interfaces/document-files';
import {
  driverCdlDefault,
  driverCdlDefaultPolygon,
  driverCdlDefaultVerification,
} from 'common/document/ui/driver-document';
import { AnimatedLoaderModal } from 'components/Loaders/AnimatedLoader';
import { clientDateToServerDateString } from 'functions/date/clientDateToServerDate';
import { buildFieldPolygon } from '../Utils/field-polygon';
import { buildVerificationResult } from '../Utils/verification-result';
import { buildFieldVerified } from '../Utils/field-verified';
import { IUpdateDocumentImage } from '../DocumentReviewContainer';
import { DocumentImageCropper } from '../DocumentImageCropper';
import { validationSchema } from './validationSchema';
import { FileDTO } from 'store/candidate/interfaces';

import styles from './styles.module.scss';
import { OriginalFileParams } from '../DocumentReviewContainer/types';
import { defaultOriginalFileParams } from '../DocumentReviewContainer/constants';
import { buildVerificationStatus } from '../Utils/verification-status';
import clsx from 'clsx';
import { CompanyContext } from '../../../../index';
import { DriverLicenseFormShort } from '../DriverLicenseForm/DriverLicenseFormShort';
import { canDownloadDriverLicense } from '../DOTMedicalCardView/Utils/can-download-driver-license';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';
import { ReactComponent as DocumentHiddenIcon } from '../../../../assets/images/document-hidden.svg';
import { managerDocumentService } from 'store/manager-document';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';

type InputProps = {
  document: IDocument;
  onReject?: (reason: IRejectReasonDocument) => void;
  onApprove?: (data: { [key: string]: any }) => void;
  onDownload?: () => void;
  onPurge?: (document: IDocument) => void;
  onUpdateDocumentImage?: (data: IUpdateDocumentImage) => void;
  titlePrev: string;
  titleNext: string;
  onPrev: () => void;
  onNext: () => void;
  setOriginalFileParams: (v: OriginalFileParams) => void;
  currentOriginalFile?: FileDTO;
  isLoadingOriginalFile: boolean;
  containsUserPhoto?: boolean;
  setContainsUserPhoto?: (v: boolean) => void;
};

export const DriverLicenseView = ({
  onReject,
  onApprove,
  document,
  onDownload,
  onPurge,
  onUpdateDocumentImage,
  titlePrev,
  titleNext,
  onPrev,
  onNext,
  setOriginalFileParams,
  currentOriginalFile,
  isLoadingOriginalFile,
  containsUserPhoto = true,
  setContainsUserPhoto,
}: InputProps) => {
  const { isReadOnly } = useManager();
  const { id: candidateId } = useParams<{ id: string }>();
  const company = useContext(CompanyContext);
  const [polygon, setPolygon] = useState<any | null>();
  const [locked, setLocked] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [currentDocumentFile, setCurrentDocumentFile] = useState<
    DocumentFiles | undefined
  >();

  const { data: candidatePhotoIdDocument } =
    managerDocumentService.endpoints.getLastDocumentByCandidateIdAndDocumentType.useQuery(
      {
        candidateId: candidateId || '',
        documentType: DocumentTypeEnum.PHOTO_ID,
      },
      { skip: !candidateId }
    );

  const canDownload = useMemo(() => {
    return canDownloadDriverLicense({
      document,
      photoIdDocument: candidatePhotoIdDocument,
    });
  }, [document, candidatePhotoIdDocument]);

  const handleCloseEditMode = () => {
    setIsEdit(false);
    setOriginalFileParams(defaultOriginalFileParams);
  };

  useEffect(() => {
    handleCloseEditMode();
  }, [document]);

  useEffect(() => {
    setLocked(
      ![DocumentStatusEnum.UPLOADED, DocumentStatusEnum.NEEDS_REVIEW].includes(
        document.status
      )
    );
  }, [document]);

  const documentFileList = useMemo(() => {
    return document.documentFiles.filter(
      (docFile) => !docFile.documentName.endsWith(originalSuffix)
    );
  }, [document.documentFiles]);

  const initialValues = useMemo(() => {
    if (!document?.details || !Object.keys(document.details).length) {
      return driverCdlDefault;
    }

    return document.details.reduce(
      (hash: { [key: string]: any }, currentValue: IDocumentDetailField) => {
        if (
          currentValue?.fieldValue &&
          !Number.isNaN(Date.parse(currentValue.fieldValue))
        ) {
          const [date] = currentValue.fieldValue.split('T');
          const [y, m, d] = date.split('-');
          hash[currentValue.fieldName] = `${m}/${d}/${y}`;
        } else {
          hash[currentValue.fieldName] = currentValue?.fieldValue || '';
        }
        return hash;
      },
      {}
    );
  }, [document]);

  const fieldVerified = useMemo(() => {
    const fieldVerified = buildFieldVerified(document?.details);
    return fieldVerified ?? driverCdlDefaultVerification;
  }, [document]);

  const fieldPolygon = useMemo(() => {
    const fieldPolygon = buildFieldPolygon(document?.details);
    return fieldPolygon ?? driverCdlDefaultPolygon;
  }, [document]);

  const verificationResult = useMemo(() => {
    return buildVerificationResult(document?.details);
  }, [document]);

  const verificationStatus = useMemo(() => {
    return buildVerificationStatus(document?.details);
  }, [document]);

  const { getFieldProps, getFieldErrorProp, handleSubmit, isValid } = useFormik(
    {
      initialValues,
      enableReinitialize: true,
      validationSchema,
      onSubmit(value: { [key: string]: any }) {
        onApprove &&
          onApprove({
            ...value,
            dateOfBirth: value.dateOfBirth
              ? clientDateToServerDateString(new Date(value.dateOfBirth))
              : undefined,
            issueDate: value.issueDate
              ? clientDateToServerDateString(new Date(value.issueDate))
              : undefined,
            expireDate: value.expireDate
              ? clientDateToServerDateString(new Date(value.expireDate))
              : undefined,
          });
      },
    }
  );

  const handleApprove = () => {
    handleSubmit();
  };

  const handlePurge = () => {
    onPurge && onPurge(document);
  };

  const handleHoverField = (polygon: any | null) => {
    setPolygon(polygon);
  };

  const handleUnHoverField = () => {
    setPolygon(null);
  };

  const handleEdit = () => {
    if (currentDocumentFile?.documentName) {
      setOriginalFileParams({
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile?.documentName,
      });
      setIsEdit(true);
    }
  };

  const handleSaveImage = (file: Blob) => {
    handleCloseEditMode();
    if (
      onUpdateDocumentImage &&
      file &&
      document?.candidateDocumentId &&
      currentDocumentFile?.documentName
    ) {
      onUpdateDocumentImage({
        file: new File([file], currentDocumentFile?.documentName, {
          type: file.type,
        }),
        candidateDocumentId: document.candidateDocumentId,
        fileName: currentDocumentFile.documentName,
      });
    }
  };

  if (document.status === DocumentStatusEnum.DELETED) {
    return (
      <div className={styles.root}>
        <Divider />
        <DocumentReviewHeader
          titlePrev={titlePrev}
          titleNext={titleNext}
          onPrev={onPrev}
          onNext={onNext}
          title={document.documentName}
          notifications={[]}
          canEditDocumentType={false}
          document={document}
        />
        <div className={styles.content}>
          <div className={styles.purged}>The document has been purged</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Divider />
      <DocumentReviewHeader
        titlePrev={titlePrev}
        titleNext={titleNext}
        onPrev={onPrev}
        onNext={onNext}
        title={
          document.documentName +
          (document.status === DocumentStatusEnum.REJECTED ? ' - REJECTED' : '')
        }
        notifications={[]}
        canEditDocumentType={false}
        document={document}
      />
      <div className={styles.content}>
        {isReadOnly ? (
          readOnlyViewableStatuses.includes(document.status) ? (
            <DocumentImageViewer
              polygon={polygon}
              currentDocument={currentDocumentFile}
              setCurrentDocument={setCurrentDocumentFile}
              documents={documentFileList}
            />
          ) : (
            <DocumentHiddenIcon />
          )
        ) : (
          <>
            {isEdit ? (
              !isLoadingOriginalFile && currentOriginalFile?.url ? (
                <DocumentImageCropper
                  fileUrl={currentOriginalFile?.url}
                  onSave={handleSaveImage}
                  onClose={handleCloseEditMode}
                  aspectRatio={1.58}
                />
              ) : (
                <AnimatedLoaderModal isLoading={isLoadingOriginalFile} />
              )
            ) : (
              <>
                <div
                  className={clsx(
                    styles.cropper,
                    Boolean(
                      company?.documentProcessingConfigurations
                        ?.skipCdlExtraction
                    ) && styles.fullWidth
                  )}
                >
                  <DocumentImageViewer
                    onDownload={
                      canDownload.status !== 'fail' ? onDownload : undefined
                    }
                    onPurge={handlePurge}
                    onReject={!locked ? onReject : undefined}
                    onEdit={
                      currentDocumentFile?.fileUrl ? handleEdit : undefined
                    }
                    onApprove={!locked ? handleApprove : undefined}
                    polygon={polygon}
                    currentDocument={currentDocumentFile}
                    setCurrentDocument={setCurrentDocumentFile}
                    documents={documentFileList}
                    canApprove={{
                      status: isValid,
                      helpText: isValid ? '' : 'Fill out the form',
                    }}
                    canDownload={canDownload}
                  />
                </div>
                {!company?.documentProcessingConfigurations
                  ?.skipCdlExtraction ? (
                  <div className={styles.formWrapper}>
                    <DriverLicenseForm
                      getFieldProps={getFieldProps}
                      getFieldErrorProp={getFieldErrorProp}
                      fieldVerified={fieldVerified}
                      fieldPolygon={fieldPolygon}
                      verificationResult={verificationResult || undefined}
                      verificationStatus={verificationStatus || undefined}
                      onHoverField={handleHoverField}
                      onUnHoverField={handleUnHoverField}
                      disabled={locked}
                      containsUserPhoto={containsUserPhoto}
                      setContainsUserPhoto={setContainsUserPhoto}
                    />
                  </div>
                ) : (
                  <div className={styles.formWrapper}>
                    <DriverLicenseFormShort
                      getFieldProps={getFieldProps}
                      getFieldErrorProp={getFieldErrorProp}
                      fieldVerified={fieldVerified}
                      fieldPolygon={fieldPolygon}
                      verificationResult={verificationResult || undefined}
                      verificationStatus={verificationStatus || undefined}
                      onHoverField={handleHoverField}
                      onUnHoverField={handleUnHoverField}
                      disabled={locked}
                      containsUserPhoto={containsUserPhoto}
                      setContainsUserPhoto={setContainsUserPhoto}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <LogEvents logs={document.documentEvents} />
    </div>
  );
};
